import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import { toast } from "sonner";
import { Upload, Loader2, FileText, ClipboardList, FileCheck2 } from "lucide-react";

export default function ApplyDialog({ job, open, onOpenChange, onSuccess }) {
  const { t } = useI18n();
  const { user, token, setSession, authHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("cv"); // 'cv' or 'form'
  const [profileCv, setProfileCv] = useState(null); // {id, original_filename}
  const [useProfileCv, setUseProfileCv] = useState(true);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    password: "",
    cover_letter: "",
    salary_expectation: "",
    experience_summary: user?.experience_summary || "",
    work_history: user?.work_history || "",
    education: user?.education || "",
    skills: user?.skills || "",
    current_location: "",
    work_eligible_mauritius: "",
    requires_sponsorship: "",
    country: "",
  });
  const [cv, setCv] = useState(null);

  // Fetch profile CV when authenticated candidate opens dialog
  useEffect(() => {
    if (open && user && user.role === "candidate") {
      axios.get(`${API}/candidate/profile`, { headers: authHeader }).then(r => {
        setProfileCv(r.data.cv || null);
        setUseProfileCv(!!r.data.cv);
        const p = r.data.profile || {};
        setForm(f => ({
          ...f,
          name: p.name || f.name,
          email: p.email || f.email,
          phone: p.phone || f.phone,
          experience_summary: p.experience_summary || f.experience_summary,
          work_history: p.work_history || f.work_history,
          education: p.education || f.education,
          skills: p.skills || f.skills,
        }));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "cv" && !useProfileCv && !cv) { toast.error("Please attach your CV or use the profile one"); return; }
    if (mode === "cv" && useProfileCv && !profileCv) { toast.error("No CV on file. Upload one or use the form."); return; }
    if (mode === "form" && !form.experience_summary && !form.work_history) {
      toast.error("Please fill the experience or work history"); return;
    }
    if (!form.current_location) { toast.error(t("apply.needsLocation") || "Please tell us where you're currently based"); return; }
    if (!form.work_eligible_mauritius) { toast.error(t("apply.needsWorkAuth") || "Please answer the work-authorisation question"); return; }
    if (!form.requires_sponsorship) { toast.error(t("apply.needsSponsorship") || "Please answer the sponsorship question"); return; }
    setLoading(true);
    try {
      axios.post(`${API}/jobs/${job.id}/click`).catch(() => {});
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      if (form.password) fd.append("password", form.password);
      if (form.cover_letter) fd.append("cover_letter", form.cover_letter);
      if (form.salary_expectation) fd.append("salary_expectation", form.salary_expectation);
      fd.append("current_location", form.current_location);
      fd.append("work_eligible_mauritius", form.work_eligible_mauritius);
      fd.append("requires_sponsorship", form.requires_sponsorship);
      if (form.country) fd.append("country", form.country);
      if (mode === "cv") {
        if (useProfileCv && profileCv) {
          fd.append("use_profile_cv", "true");
        } else if (cv) {
          fd.append("cv", cv);
        }
      }
      if (mode === "form") {
        if (form.experience_summary) fd.append("experience_summary", form.experience_summary);
        if (form.work_history) fd.append("work_history", form.work_history);
        if (form.education) fd.append("education", form.education);
        if (form.skills) fd.append("skills", form.skills);
      }
      const headers = token ? { ...authHeader, "Content-Type": "multipart/form-data" } : { "Content-Type": "multipart/form-data" };
      const res = await axios.post(`${API}/jobs/${job.id}/apply`, fd, { headers });
      toast.success(t("apply.success"));
      if (res.data.auto_created && res.data.token) {
        setSession(res.data.token, null);
        const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.token}` } });
        setSession(null, me.data);
      }
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("apply.title")}</DialogTitle>
          <DialogDescription>{job?.title} · {job?.company_name}</DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="cv" data-testid="apply-mode-cv"><FileText className="h-4 w-4 mr-2" />{t("apply.withCv")}</TabsTrigger>
            <TabsTrigger value="form" data-testid="apply-mode-form"><ClipboardList className="h-4 w-4 mr-2" />{t("apply.withoutCv")}</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="ap-name">{t("apply.name")}</Label>
              <Input id="ap-name" data-testid="apply-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ap-email">{t("apply.email")}</Label>
                <Input id="ap-email" data-testid="apply-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ap-phone">{t("apply.phone")}</Label>
                <Input id="ap-phone" data-testid="apply-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            {/* Eligibility questions (compulsory) */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid="apply-eligibility-section">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {t("apply.eligibilityHeader") || "A few quick questions"}
              </div>
              <div>
                <Label>{t("apply.q1") || "Where are you currently based?"} *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[{v:"mauritius",l:"Mauritius"},{v:"rodrigues",l:"Rodrigues"},{v:"abroad",l:t("apply.abroad")||"Abroad"}].map(o => (
                    <button
                      type="button"
                      key={o.v}
                      onClick={() => setForm({ ...form, current_location: o.v })}
                      data-testid={`apply-loc-${o.v}`}
                      className={`h-9 text-xs rounded-full border transition ${form.current_location===o.v ? "bg-[#1A8FE3] text-white border-[#1A8FE3]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1A8FE3]"}`}
                    >{o.l}</button>
                  ))}
                </div>
              </div>
              {form.current_location === "abroad" && (
                <div>
                  <Label>{t("apply.country") || "Which country?"}</Label>
                  <Input data-testid="apply-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. South Africa" />
                </div>
              )}
              <div>
                <Label>{t("apply.q2") || "Are you currently authorised to work in Mauritius?"} *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[{v:"true",l:t("apply.yes")||"Yes"},{v:"false",l:t("apply.no")||"No"}].map(o => (
                    <button type="button" key={o.v} onClick={() => setForm({ ...form, work_eligible_mauritius: o.v })} data-testid={`apply-eligible-${o.v}`}
                      className={`h-9 text-xs rounded-full border transition ${form.work_eligible_mauritius===o.v ? "bg-[#1A8FE3] text-white border-[#1A8FE3]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1A8FE3]"}`}
                    >{o.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("apply.q3") || "Do you require employer sponsorship or a work permit?"} *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[{v:"true",l:t("apply.yes")||"Yes"},{v:"false",l:t("apply.no")||"No"}].map(o => (
                    <button type="button" key={o.v} onClick={() => setForm({ ...form, requires_sponsorship: o.v })} data-testid={`apply-sponsor-${o.v}`}
                      className={`h-9 text-xs rounded-full border transition ${form.requires_sponsorship===o.v ? "bg-[#1A8FE3] text-white border-[#1A8FE3]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1A8FE3]"}`}
                    >{o.l}</button>
                  ))}
                </div>
              </div>
            </div>
            {!user && (
              <div>
                <Label htmlFor="ap-pw">{t("apply.password")}</Label>
                <Input id="ap-pw" data-testid="apply-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
                <p className="text-xs text-slate-500 mt-1">{t("apply.passwordHint")}</p>
              </div>
            )}

            <TabsContent value="cv" className="m-0 space-y-3">
              {profileCv && (
                <label className={`flex items-center justify-between gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${useProfileCv ? "border-[#1A8FE3] bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="apply-use-profile-cv">
                  <span className="flex items-center gap-3">
                    <FileCheck2 className={`h-5 w-5 ${useProfileCv ? "text-[#1A8FE3]" : "text-slate-400"}`} />
                    <span>
                      <span className="block text-sm font-medium text-slate-900">{t("apply.profileCv")}</span>
                      <span className="block text-xs text-slate-500">{t("apply.profileCvOnFile")} {profileCv.original_filename}</span>
                    </span>
                  </span>
                  <input type="radio" checked={useProfileCv} onChange={() => { setUseProfileCv(true); setCv(null); }} className="h-4 w-4 accent-[#1A8FE3]" />
                </label>
              )}
              <label className={`flex items-center gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${!useProfileCv || !profileCv ? "border-[#1A8FE3] bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
                <Upload className={`h-5 w-5 ${!useProfileCv || !profileCv ? "text-[#1A8FE3]" : "text-slate-400"}`} />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">{profileCv ? t("apply.uploadNew") : t("apply.cv")}</span>
                  <span className="block text-xs text-slate-500">{cv ? cv.name : "PDF, DOC, DOCX (max 10MB)"}</span>
                </span>
                <input data-testid="apply-cv" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { setCv(e.target.files?.[0] || null); setUseProfileCv(false); }} />
              </label>
            </TabsContent>

            <TabsContent value="form" className="m-0 space-y-4">
              <div>
                <Label>{t("apply.experience")} *</Label>
                <Textarea data-testid="apply-experience" rows={3} value={form.experience_summary} onChange={(e) => setForm({ ...form, experience_summary: e.target.value })} placeholder={t("apply.experiencePh")} />
              </div>
              <div>
                <Label>{t("apply.workHistory")}</Label>
                <Textarea data-testid="apply-history" rows={4} value={form.work_history} onChange={(e) => setForm({ ...form, work_history: e.target.value })} placeholder={t("apply.workHistoryPh")} />
              </div>
              <div>
                <Label>{t("apply.education")}</Label>
                <Textarea data-testid="apply-education" rows={2} value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} placeholder={t("apply.educationPh")} />
              </div>
              <div>
                <Label>{t("apply.skills")}</Label>
                <Input data-testid="apply-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder={t("apply.skillsPh")} />
              </div>
            </TabsContent>

            <div>
              <Label htmlFor="ap-cover">{t("apply.motivations")}</Label>
              <Textarea id="ap-cover" data-testid="apply-cover" rows={3} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder={t("apply.motivationsPh")} />
            </div>

            <div>
              <Label htmlFor="ap-salary">{t("apply.salaryExpectation")}</Label>
              <Input id="ap-salary" data-testid="apply-salary" value={form.salary_expectation} onChange={(e) => setForm({ ...form, salary_expectation: e.target.value })} placeholder={t("apply.salaryExpectationPh")} />
            </div>

            <Button type="submit" disabled={loading} data-testid="apply-submit" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] rounded-full text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("apply.submit")}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
